import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import "./App.css";
import { translations } from "./i18n";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { BriefingExperience } from "./components/BriefingExperience";
import { FinalState } from "./components/FinalState";
import { Overlays } from "./components/Overlays";

export default function App() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialView = ["briefing", "final"].includes(params.get("view")) ? params.get("view") : "home";
  const seed = params.get("seed") || "";

  const [stage, setStage] = useState(initialView); // home | briefing | final
  const [lang, setLang] = useState(params.get("lang") === "pt" ? "pt" : "en");
  const [overlay, setOverlay] = useState(null); // about | contact | null
  const [caseId, setCaseId] = useState("");
  const [finalMap, setFinalMap] = useState(null);

  const t = translations[lang];

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const goHome = () => {
    setOverlay(null);
    setStage("home");
  };

  return (
    <div className="min-h-screen bg-bh-bg text-bh-ink relative">
      <div className="noise" />

      <Header
        t={t}
        lang={lang}
        setLang={setLang}
        onBrand={goHome}
        onAbout={() => setOverlay("about")}
        dim={stage === "final"}
      />

      <AnimatePresence mode="wait">
        {stage === "home" && (
          <motion.main
            key="home"
            exit={{ opacity: 0, filter: "blur(4px)" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Hero t={t} onTalk={() => setStage("briefing")} />
          </motion.main>
        )}

        {stage === "briefing" && (
          <motion.main
            key="briefing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, filter: "blur(6px)" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <BriefingExperience t={t} lang={lang} seed={seed} forceContact={params.get("contact") === "1"} onDone={(id, map) => { setCaseId(id); setFinalMap(map); setStage("final"); }} />
          </motion.main>
        )}

        {stage === "final" && (
          <motion.main key="final" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FinalState t={t} caseId={caseId} mapData={finalMap} onReturn={goHome} />
          </motion.main>
        )}
      </AnimatePresence>

      <Overlays view={overlay} t={t} onClose={() => setOverlay(null)} />
    </div>
  );
}
